import { useEffect, useState } from 'react';
import type { AccountBalance } from '../types/trade';
import { api } from '../services/api';
import { toDisplayAccountBalances } from './accountBalancesModel';

interface AccountBalancesProps {
  refreshInterval?: number;
}

export function AccountBalances({ refreshInterval = 30000 }: AccountBalancesProps) {
  const [balances, setBalances] = useState<AccountBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBalances();
    const interval = setInterval(loadBalances, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadBalances = async () => {
    try {
      const response = await api.getBalances();
      setBalances(toDisplayAccountBalances(response));
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load balances');
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatToken = (value: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const getTotalUsd = () => balances.reduce((sum, b) => sum + b.totalUsd, 0);
  const getTotalFco = () => balances.reduce((sum, b) => sum + b.fco, 0);
  const getTotalUsdt = () => balances.reduce((sum, b) => sum + b.usdt, 0);

  if (loading && balances.length === 0) {
    return <div className="account-balances loading">Loading balances...</div>;
  }

  if (error) {
    return <div className="account-balances error">{error}</div>;
  }

  return (
    <div className="account-balances">
      <div className="balances-header">
        <h3>Account Balances</h3>
        <button onClick={loadBalances} className="refresh-btn" title="Refresh">
          ↻
        </button>
      </div>

      <div className="balances-table">
        <div className="table-header">
          <span>Account</span>
          <span>FCO</span>
          <span>USDT</span>
          <span>Total USD</span>
        </div>

        {balances.map((balance) => (
          <div key={balance.account} className="balance-row">
            <span className="account-name">{balance.account}</span>
            <span className="fco">{formatToken(balance.fco)}</span>
            <span className="usdt">{formatCurrency(balance.usdt)}</span>
            <span className="total">{formatCurrency(balance.totalUsd)}</span>
          </div>
        ))}

        <div className="balance-row total-row">
          <span className="account-name">TOTAL</span>
          <span className="fco">{formatToken(getTotalFco())}</span>
          <span className="usdt">{formatCurrency(getTotalUsdt())}</span>
          <span className="total">{formatCurrency(getTotalUsd())}</span>
        </div>
      </div>

      {balances[0]?.lastUpdated && (
        <div className="last-updated">
          Last updated: {new Date(balances[0].lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
}
